import React from "react";
import { httpsCallable } from "firebase/functions";
import { functions, db } from "@/lib/firebase";
import { ToastContext } from "@/components/ToastProvider.jsx";
import { formatDT } from "@/utils/dates";
import { collection, onSnapshot, query, orderBy, limit } from "firebase/firestore";
import { 
  TrendingUp, 
  TrendingDown, 
  Users,
  DollarSign,
  Package,
  Clock,
  Star,
  Award,
  BarChart3,
  Download,
  Filter,
  Search,
  RefreshCw,
  Eye
} from "lucide-react";

function StatCard({ title, value, change, icon: Icon, trend, color = "blue" }) {
  const colors = {
    blue: { bg: "bg-blue-100", text: "text-blue-600", trend: "text-blue-600" },
    green: { bg: "bg-green-100", text: "text-green-600", trend: "text-green-600" },
    yellow: { bg: "bg-yellow-100", text: "text-yellow-600", trend: "text-yellow-600" },
    red: { bg: "bg-red-100", text: "text-red-600", trend: "text-red-600" },
    purple: { bg: "bg-purple-100", text: "text-purple-600", trend: "text-purple-600" }
  };
  
  const colorClass = colors[color] || colors.blue;

  return (
    <div className="card p-6">
      <div className="flex items-center justify-between">
        <div className="flex-1">
          <p className="text-sm font-medium text-gray-600">{title}</p>
          <p className="text-3xl font-bold text-gray-900 mt-2">{value}</p>
          {change && (
            <div className={`flex items-center mt-2 text-sm ${colorClass.trend}`}>
              {trend === 'up' ? <TrendingUp className="w-4 h-4 mr-1" /> : 
               trend === 'down' ? <TrendingDown className="w-4 h-4 mr-1" /> : null}
              <span>{change}</span>
            </div>
          )}
        </div>
        <div className={`p-3 ${colorClass.bg} rounded-full`}>
          <Icon className={`w-6 h-6 ${colorClass.text}`} />
        </div>
      </div>
    </div>
  );
}

function RiderPerformanceTable({ riders, onViewDetails }) {
  if (!riders || riders.length === 0) {
    return (
      <div className="text-center py-8">
        <Users className="w-12 h-12 text-gray-400 mx-auto mb-3" />
        <p className="text-gray-500">No rider data available</p>
      </div>
    );
  }

  return (
    <div className="overflow-x-auto">
      <table className="min-w-full divide-y divide-gray-200">
        <thead className="bg-gray-50">
          <tr>
            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
              Rider
            </th>
            <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
              Deliveries
            </th>
            <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
              Earnings
            </th>
            <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
              Avg Time
            </th>
            <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
              Rating
            </th>
            <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
              Performance
            </th>
            <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
              Actions
            </th>
          </tr>
        </thead>
        <tbody className="bg-white divide-y divide-gray-200">
          {riders.map((rider, index) => (
            <tr key={rider.riderId} className={index % 2 === 0 ? "bg-white" : "bg-gray-50"}>
              <td className="px-6 py-4 whitespace-nowrap">
                <div className="flex items-center">
                  <div className="flex-shrink-0 h-10 w-10">
                    <div className="h-10 w-10 rounded-full bg-gray-300 flex items-center justify-center">
                      <span className="text-sm font-medium text-gray-700">
                        {(rider.riderName || rider.riderId || 'U').charAt(0).toUpperCase()}
                      </span>
                    </div>
                  </div>
                  <div className="ml-4">
                    <div className="text-sm font-medium text-gray-900">
                      {rider.riderName || "Unknown Rider"}
                    </div>
                    <div className="text-sm text-gray-500">
                      #{rider.rank || 'Unranked'}
                    </div>
                  </div>
                </div>
              </td>
              <td className="px-6 py-4 whitespace-nowrap text-right text-sm text-gray-900">
                {rider.deliveriesCompleted || 0}
              </td>
              <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium text-gray-900">
                ₱{Number(rider.totalEarnings || 0).toFixed(2)}
              </td>
              <td className="px-6 py-4 whitespace-nowrap text-right text-sm text-gray-900">
                {rider.averageDeliveryTime || 0} min
              </td>
              <td className="px-6 py-4 whitespace-nowrap text-right text-sm text-gray-900">
                <div className="flex items-center justify-end">
                  <Star className="w-4 h-4 text-yellow-400 mr-1" />
                  {Number(rider.customerRating || 0).toFixed(1)}
                </div>
              </td>
              <td className="px-6 py-4 whitespace-nowrap text-right text-sm text-gray-900">
                <div className={`inline-flex px-2 py-1 rounded-full text-xs font-medium ${
                  (rider.performanceScore || 0) >= 80 
                    ? "bg-green-100 text-green-800"
                    : (rider.performanceScore || 0) >= 60
                    ? "bg-yellow-100 text-yellow-800"
                    : "bg-red-100 text-red-800"
                }`}>
                  {rider.performanceScore || 0}%
                </div>
              </td>
              <td className="px-6 py-4 whitespace-nowrap text-center text-sm font-medium">
                <button
                  className="text-blue-600 hover:text-blue-900"
                  onClick={() => onViewDetails(rider)}
                >
                  <Eye className="w-4 h-4" />
                </button>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

export default function RiderAnalyticsDashboard() {
  const { notify } = React.useContext(ToastContext);
  
  const [analytics, setAnalytics] = React.useState(null);
  const [ridersData, setRidersData] = React.useState([]);
  const [loading, setLoading] = React.useState(true);
  const [error, setError] = React.useState("");
  const [dateRange, setDateRange] = React.useState("week");
  const [searchTerm, setSearchTerm] = React.useState("");
  const [sortBy, setSortBy] = React.useState("performance");
  const [selectedRider, setSelectedRider] = React.useState(null);
  const [detailsModal, setDetailsModal] = React.useState({ open: false, rider: null });

  // Load analytics data
  React.useEffect(() => {
    async function loadAnalytics() {
      try {
        setLoading(true);
        const fn = httpsCallable(functions, "getAdminAnalytics");
        const res = await fn({ dateRange });
        
        if (res?.data) {
          setAnalytics(res.data.overview);
          setRidersData(res.data.riders || []);
        }
        
        setError("");
      } catch (e) {
        setError(e?.message || "Failed to load analytics");
        notify(e?.message || "Failed to load analytics", { type: "error" });
      } finally {
        setLoading(false);
      }
    }

    loadAnalytics();
  }, [dateRange, notify]);

  function handleViewDetails(rider) {
    setDetailsModal({ open: true, rider });
  }

  function closeDetailsModal() {
    setDetailsModal({ open: false, rider: null });
  }

  async function refreshData() {
    try {
      setLoading(true);
      const fn = httpsCallable(functions, "getAdminAnalytics");
      const res = await fn({ dateRange, forceRefresh: true });
      
      if (res?.data) {
        setAnalytics(res.data.overview);
        setRidersData(res.data.riders || []);
      }
      
      notify("Analytics data refreshed", { type: "success" });
    } catch (e) {
      notify(e?.message || "Failed to refresh data", { type: "error" });
    } finally {
      setLoading(false);
    }
  }

  // Filter and sort riders
  const filteredRiders = React.useMemo(() => {
    let filtered = ridersData.filter(rider => 
      !searchTerm || 
      (rider.riderName || "").toLowerCase().includes(searchTerm.toLowerCase()) ||
      rider.riderId.toLowerCase().includes(searchTerm.toLowerCase())
    );

    // Sort riders
    filtered.sort((a, b) => {
      switch (sortBy) {
        case "performance":
          return (b.performanceScore || 0) - (a.performanceScore || 0);
        case "deliveries":
          return (b.deliveriesCompleted || 0) - (a.deliveriesCompleted || 0);
        case "earnings":
          return (b.totalEarnings || 0) - (a.totalEarnings || 0);
        case "rating":
          return (b.customerRating || 0) - (a.customerRating || 0);
        case "time":
          return (a.averageDeliveryTime || 999) - (b.averageDeliveryTime || 999);
        default:
          return (b.performanceScore || 0) - (a.performanceScore || 0);
      }
    });

    return filtered;
  }, [ridersData, searchTerm, sortBy]);

  function getDateRangeLabel() {
    switch (dateRange) {
      case "week": return "This Week";
      case "month": return "This Month";
      case "quarter": return "This Quarter";
      case "year": return "This Year";
      default: return "This Week";
    }
  }

  if (loading) {
    return (
      <div className="space-y-6">
        <div className="text-center py-12">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto"></div>
          <p className="text-gray-500 mt-2">Loading analytics...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="space-y-6">
        <div className="bg-red-50 border border-red-200 rounded-lg p-4">
          <div className="flex items-center gap-2 text-red-700">
            <BarChart3 className="w-5 h-5" />
            <span>{error}</span>
          </div>
        </div>
      </div>
    );
  }

  const {
    totalRiders = 0,
    activeRiders = 0,
    totalDeliveries = 0,
    totalEarnings = 0,
    averageRating = 0,
    averageDeliveryTime = 0
  } = analytics || {};

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h2 className="text-2xl font-bold text-gray-900">Rider Analytics Dashboard</h2>
          <p className="text-gray-600 mt-1">Monitor rider performance and system metrics</p>
        </div>
        <div className="flex gap-2">
          <button 
            className="btn btn-outline"
            onClick={refreshData}
            disabled={loading}
          >
            <RefreshCw className={`w-4 h-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
            Refresh
          </button>
          <select
            className="input"
            value={dateRange}
            onChange={(e) => setDateRange(e.target.value)}
          >
            <option value="week">This Week</option>
            <option value="month">This Month</option>
            <option value="quarter">This Quarter</option>
            <option value="year">This Year</option>
          </select>
        </div>
      </div>

      {/* Overview Statistics */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-6 gap-6">
        <StatCard
          title="Total Riders"
          value={totalRiders}
          icon={Users}
          color="blue"
        />
        
        <StatCard
          title="Active Riders"
          value={activeRiders}
          change="+12% this period"
          trend="up"
          icon={Users}
          color="green"
        />
        
        <StatCard
          title="Total Deliveries"
          value={totalDeliveries}
          change="+8% this period"
          trend="up"
          icon={Package}
          color="purple"
        />
        
        <StatCard
          title="Total Earnings"
          value={`₱${totalEarnings.toFixed(2)}`}
          change="+15% this period"
          trend="up"
          icon={DollarSign}
          color="green"
        />
        
        <StatCard
          title="Avg Rating"
          value={averageRating.toFixed(1)}
          change="↗ 0.2 points"
          trend="up"
          icon={Star}
          color="yellow"
        />
        
        <StatCard
          title="Avg Delivery Time"
          value={`${averageDeliveryTime} min`}
          change="↘ 5 min faster"
          trend="up"
          icon={Clock}
          color="blue"
        />
      </div>

      {/* Filters and Search */}
      <div className="card p-6">
        <div className="flex flex-col sm:flex-row gap-4">
          <div className="flex-1">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
              <input
                type="text"
                placeholder="Search riders..."
                className="input pl-10 w-full"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
            </div>
          </div>
          <div className="flex gap-2">
            <select
              className="input"
              value={sortBy}
              onChange={(e) => setSortBy(e.target.value)}
            >
              <option value="performance">Sort by Performance</option>
              <option value="deliveries">Sort by Deliveries</option>
              <option value="earnings">Sort by Earnings</option>
              <option value="rating">Sort by Rating</option>
              <option value="time">Sort by Speed</option>
            </select>
            <button className="btn btn-outline">
              <Download className="w-4 h-4 mr-2" />
              Export
            </button>
          </div>
        </div>
      </div>

      {/* Rider Performance Table */}
      <div className="card">
        <div className="px-6 py-4 border-b border-gray-200">
          <h3 className="text-lg font-semibold text-gray-900">Rider Performance - {getDateRangeLabel()}</h3>
        </div>
        <div className="p-6">
          <RiderPerformanceTable 
            riders={filteredRiders} 
            onViewDetails={handleViewDetails}
          />
        </div>
      </div>

      {/* Rider Details Modal */}
      {detailsModal.open && detailsModal.rider && (
        <div className="fixed inset-0 z-[1100]" role="dialog" aria-modal="true">
          <div className="absolute inset-0 bg-black/40 backdrop-blur-[1px]" onClick={closeDetailsModal} />
          <div className="absolute inset-0 flex items-center justify-center p-4">
            <div className="card w-full max-w-2xl p-6 relative max-h-[90vh] overflow-y-auto">
              <button 
                className="absolute right-4 top-4 p-2 rounded hover:bg-gray-100" 
                onClick={closeDetailsModal}
              >
                ✕
              </button>
              
              <h3 className="text-lg font-semibold text-gray-900 mb-4">
                {detailsModal.rider.riderName || "Unknown Rider"} - Performance Details
              </h3>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
                <div className="bg-blue-50 p-4 rounded-lg">
                  <p className="text-sm font-medium text-gray-600">Deliveries Completed</p>
                  <p className="text-2xl font-bold text-blue-600">
                    {detailsModal.rider.deliveriesCompleted || 0}
                  </p>
                </div>
                <div className="bg-green-50 p-4 rounded-lg">
                  <p className="text-sm font-medium text-gray-600">Total Earnings</p>
                  <p className="text-2xl font-bold text-green-600">
                    ₱{Number(detailsModal.rider.totalEarnings || 0).toFixed(2)}
                  </p>
                </div>
                <div className="bg-yellow-50 p-4 rounded-lg">
                  <p className="text-sm font-medium text-gray-600">Average Delivery Time</p>
                  <p className="text-2xl font-bold text-yellow-600">
                    {detailsModal.rider.averageDeliveryTime || 0} min
                  </p>
                </div>
                <div className="bg-purple-50 p-4 rounded-lg">
                  <p className="text-sm font-medium text-gray-600">Customer Rating</p>
                  <p className="text-2xl font-bold text-purple-600">
                    {Number(detailsModal.rider.customerRating || 0).toFixed(1)} / 5.0
                  </p>
                </div>
              </div>

              <div className="bg-gray-50 p-4 rounded-lg">
                <p className="text-sm font-medium text-gray-600 mb-2">Overall Performance Score</p>
                <div className="flex items-center">
                  <div className="flex-1 bg-gray-200 rounded-full h-3 mr-3">
                    <div 
                      className={`h-3 rounded-full ${
                        (detailsModal.rider.performanceScore || 0) >= 80 
                          ? "bg-green-500"
                          : (detailsModal.rider.performanceScore || 0) >= 60
                          ? "bg-yellow-500"
                          : "bg-red-500"
                      }`}
                      style={{ width: `${detailsModal.rider.performanceScore || 0}%` }}
                    />
                  </div>
                  <span className="text-lg font-bold text-gray-900">
                    {detailsModal.rider.performanceScore || 0}%
                  </span>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

